import { type ReactNode, useState } from 'react'

import { cn } from '@/utils'
import { Skeleton } from './Skeleton'

type Bbox = readonly [number, number, number, number]
type LoadState = 'loading' | 'loaded' | 'error'

interface BboxCanvasProps {
  bbox: Bbox
  preview: string | null
  ariaLabel?: string
  className?: string
  children?: ReactNode
}

// A page image with bbox overlays: the SVG viewBox is the page's own pixel bbox, so overlay
// rects drawn in that same space line up regardless of the rendered size. Shared by the
// documents viewer and the bankruptcy source viewer. Owns the image's loading and error
// states; callers pass their overlays as children.
export const BboxCanvas = ({
  bbox,
  preview,
  ariaLabel,
  className,
  children,
}: BboxCanvasProps) => {
  const [state, setState] = useState<LoadState>(preview ? 'loading' : 'error')
  const [x1, y1, x2, y2] = bbox
  const width = x2 - x1
  const height = y2 - y1

  if (state === 'error' || !preview) {
    return (
      <p className="py-10 text-center text-muted-foreground text-sm">
        Couldn't load the page image.
      </p>
    )
  }

  return (
    <div className={cn('relative', className)}>
      {state === 'loading' && (
        <Skeleton className="absolute inset-0 h-full w-full" />
      )}
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
      >
        <image
          href={preview}
          width={width}
          height={height}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
        {children}
      </svg>
    </div>
  )
}
